// src/services/tenantData.ts
import Papa from 'papaparse';
import type { TenantSlug } from '../config/tenants';

type CsvRow = Record<string, string>;

/** Dataset normalizado que consume el dashboard */
export interface NormalizedSale {
  fecha: string;
  monto: number;
  canal: string;
  sucursal: string;
  producto: string;
  cliente: string;
  raw?: CsvRow;
}

/** Ruta pública (pon la carpeta /data dentro de /public) */
const ROOT = '/data';

/** Map de archivos por tenant y tipo de dataset */
const MAP: Record<TenantSlug, Record<string, string[]>> = {
  devioz: {},
  bcp: {
    ventas: [`${ROOT}/BCP/pract_02_data_ventas.csv`],
    clientes: [`${ROOT}/BCP/pract_02_data_clientes.csv`],
    productos: [`${ROOT}/BCP/pract_02_data_productos.csv`],
    sucursales: [`${ROOT}/BCP/pract_02_data_sucursales.csv`],
    servicios: [`${ROOT}/BCP/pract_02_data_servicios.csv`],
    pagos: [`${ROOT}/BCP/pract_02_data_medios_pago.csv`],
  },
  bembos: {
    ventas: [
      `${ROOT}/Bembos/pract_09_Hechos_DetalleVenta.csv`,
      `${ROOT}/Bembos/pract_09_Data_Fact_VentaCabecera.csv`,
    ],
    productos: [`${ROOT}/Bembos/pract_09_Data_Producto.csv`],
    clientes: [`${ROOT}/Bembos/pract_09_Data_Cliente.csv`],
    sucursales: [`${ROOT}/Bembos/pract_09_Data_Sucursal.csv`],
    pagos: [`${ROOT}/Bembos/pract_09_Data_MetodoPago.csv`],
  },
  claro: {
    ventas: [
      `${ROOT}/Claro/pract_04_Data_hechos_ventas.csv`,
      `${ROOT}/Claro/pract_04_Data_detalle_venta.csv`,
    ],
    clientes: [`${ROOT}/Claro/pract_04_Data_cliente.csv`],
    productos: [`${ROOT}/Claro/pract_04_Data_producto.csv`],
    sucursales: [`${ROOT}/Claro/pract_04_Data_sucursal.csv`],
    pagos: [`${ROOT}/Claro/pract_04_Data_medio_pago.csv`],
  },
  plazavea: {
    ventas: [`${ROOT}/PlazaVea/pract_03_data_hecho_ventas.csv`],
    clientes: [`${ROOT}/PlazaVea/pract_03_data_clientes.csv`],
    productos: [`${ROOT}/PlazaVea/pract_03_data_productos.csv`],
    sucursales: [`${ROOT}/PlazaVea/pract_03_data_sucursales.csv`],
    pagos: [`${ROOT}/PlazaVea/pract_03_data_metodo_pago.csv`],
  },
  // 👇 Faltaba esta clave; así satisface el tipo TenantSlug
  gloria: {
    // Si aún no tienes CSVs, déjalo vacío o agrega rutas reales después.
    ventas: [],
    // clientes: [], productos: [], sucursales: [], pagos: [],
  },
};

async function readCsv(url: string): Promise<CsvRow[]> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`No se pudo cargar ${url} (${res.status})`);
  const txt = await res.text();
  const { data } = Papa.parse<CsvRow>(txt, { header: true, skipEmptyLines: true });
  return data;
}

function toNumber(v: unknown): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string') {
    // limpia símbolos y soporta coma decimal
    const cleaned = v.replace(/[^\d.,-]/g, '').replace(/\.(?=.*\.)/g, '').replace(',', '.');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/** Intenta hallar el campo de importe o calcularlo por (cantidad × precio) */
function pickAmount(r: CsvRow): number {
  const keysPosibles = [
    'monto','total','Total','Monto','importe','Importe','subtotal','Subtotal',
    'total_pagar','totalPagar','total_venta','totalVenta','monto_total','importe_total',
    'precio_total','PrecioTotal','precioTotal',
  ];

  for (const k of keysPosibles) {
    if (k in r) {
      const n = toNumber(r[k]);
      if (n) return n;
    }
  }

  // fallback: cantidad × precio
  const cantidadKeys = ['cantidad', 'Cantidad', 'qty', 'Qty', 'unidades', 'Unidades'];
  const precioKeys = [
    'precio', 'Precio', 'precio_unitario', 'PrecioUnitario',
    'unit_price', 'UnitPrice', 'precioUnitario'
  ];

  let cant = 0, prec = 0;
  for (const k of cantidadKeys) if (k in r) { cant = toNumber(r[k]); break; }
  for (const k of precioKeys) if (k in r) { prec = toNumber(r[k]); break; }

  const calc = cant * prec;
  return Number.isFinite(calc) ? calc : 0;
}

/** API simple para el dashboard: devuelve ventas normalizadas por tenant */
export async function getSalesDataset(tenant: TenantSlug): Promise<NormalizedSale[]> {
  const files = MAP[tenant]?.ventas ?? [];
  if (!files.length) return [];

  const rowsArr = await Promise.all(files.map(readCsv));
  const rows = rowsArr.flat();

  return rows.map((r) => ({
    fecha:
      (r.fecha as string) ||
      (r.date as string) ||
      (r.Fecha as string) ||
      (r.FECHA as string) ||
      '',
    monto: pickAmount(r),
    canal:
      (r.canal as string) ||
      (r.Canal as string) ||
      (r.canal_venta as string) ||
      (r.CanalVenta as string) ||
      '',
    sucursal:
      (r.sucursal as string) ||
      (r.Sucursal as string) ||
      (r.tienda as string) ||
      (r.Tienda as string) ||
      '',
    producto:
      (r.producto as string) ||
      (r.Producto as string) ||
      (r.item as string) ||
      (r.Item as string) ||
      '',
    cliente:
      (r.cliente as string) ||
      (r.Cliente as string) ||
      (r.id_cliente as string) ||
      (r.IdCliente as string) ||
      '',
    raw: r,
  }));
}
